/**
 * ProShip Direct v2 — drag-to-order courier priority.
 *
 * DEVIATION: no drag-and-drop library is in package.json (no dnd-kit, no react-beautiful-dnd),
 * and adding a dependency for one screen is not worth it. This uses the native HTML5 drag
 * events instead. Native drag is not keyboard-operable, so every row also carries up/down
 * buttons — those, not the drag handle, are the accessible path. If a second screen needs
 * ordering, promote this component rather than writing a third approach.
 *
 * Courier codes run long (Shadowfax_Reverse_Mile_Collective_PRZ_1), so the meta line truncates
 * rather than wrapping: two-line rows made a six-courier list taller than the pane. The full
 * value is in the row's tooltip.
 */
import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { BORDER, WHITE, LIGHT_BG, SUBTLE, NAVY, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { MONO } from './directCellStyles';
import { OwnerChip } from './chips';

const rowSx = (dragging, over) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 0.75,
  py: 0.5,
  bgcolor: dragging ? LIGHT_BG : WHITE,
  border: `1px solid ${over ? PRIMARY_BLUE : BORDER}`,
  borderRadius: RADIUS.md,
  cursor: 'grab',
  opacity: dragging ? 0.5 : 1,
  minWidth: 0,
});

const rankSx = {
  fontFamily: FONT,
  fontSize: SIZE['4xs'],
  fontWeight: WEIGHT.bold,
  color: SUBTLE,
  width: 20,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
};

const nameSx = {
  fontFamily: FONT,
  fontSize: SIZE['2xs'],
  fontWeight: WEIGHT.semibold,
  color: NAVY,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const detailSx = {
  fontFamily: MONO,
  fontSize: SIZE['4xs'],
  color: SUBTLE,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default function PriorityList({ pool, order, onChange, onRemove }) {
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  // Ordered first, then anything enabled but not yet ranked, so nothing is hidden.
  const ranked = order.map((key) => pool.find((p) => p.key === key)).filter(Boolean);
  const unranked = pool.filter((p) => !order.includes(p.key));
  const items = [...ranked, ...unranked];

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = items.map((i) => i.key);
    [next[from], next[to]] = [next[to], next[from]];
    onChange(next);
  };

  const onDrop = (targetKey) => {
    const keys = items.map((i) => i.key);
    const from = keys.indexOf(dragKey);
    const to = keys.indexOf(targetKey);
    setDragKey(null);
    setOverKey(null);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...keys];
    next.splice(to, 0, next.splice(from, 1)[0]);
    onChange(next);
  };

  if (items.length === 0) {
    return (
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE }}>
        No couriers are switched on, so there is nothing to prioritise.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.xs, minWidth: 0 }}>
      {items.map((item, i) => (
        <Box
          key={item.key}
          draggable
          onDragStart={() => setDragKey(item.key)}
          onDragEnd={() => { setDragKey(null); setOverKey(null); }}
          onDragOver={(e) => { e.preventDefault(); setOverKey(item.key); }}
          onDrop={() => onDrop(item.key)}
          sx={rowSx(dragKey === item.key, overKey === item.key && dragKey !== item.key)}
        >
          <DragIndicatorIcon sx={{ fontSize: 15, color: SUBTLE, flexShrink: 0 }} />
          <Typography sx={rankSx}>P{i + 1}</Typography>
          <Tooltip title={`${item.name} · ${item.detail}`}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={nameSx}>{item.name}</Typography>
              <Typography sx={detailSx}>{item.detail}</Typography>
            </Box>
          </Tooltip>
          <OwnerChip owner={item.owner} />
          <IconButton size='small' sx={{ p: 0.25 }} onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={`Move ${item.name} up`}>
            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size='small'
            sx={{ p: 0.25 }}
            onClick={() => move(i, i + 1)}
            disabled={i === items.length - 1}
            aria-label={`Move ${item.name} down`}
          >
            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
          </IconButton>
          {onRemove && (
            <Tooltip title='Remove from priority'>
              <IconButton size='small' sx={{ p: 0.25 }} onClick={() => onRemove(item.key)} aria-label={`Remove ${item.name}`}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
    </Box>
  );
}
